import React from 'react';
import { styled } from '@mui/material/styles';
import {
  unstable_composeClasses as composeClasses,
  generateUtilityClass,
  generateUtilityClasses,
  Icon,
  useThemeProps,
} from '@mui/material';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Warning } from '@mui/icons-material';

import BlIcon from 'components/icons/BlIcon';

const PREFIX = 'MuiStepIcon';

export function getStepIconUtilityClass(slot) {
  return generateUtilityClass(PREFIX, slot);
}

const useUtilityClasses = (ownerState) => {
  const { classes } = ownerState;

  const slots = {
    root: ['root'],
    text: ['text'],
    check: ['check'],
    active: ['active'],
    completed: ['completed'],
    error: ['error'],
    circle: ['circle'],
  };

  return composeClasses(slots, getStepIconUtilityClass, classes);
};

const stepIconClasses = generateUtilityClasses(PREFIX, [
  'root',
  'text',
  'check',
  'active',
  'completed',
  'error',
  'circle',
]);

const StyledIcon = styled(Icon)(({ theme }) => ({
  /* Styles applied to the root element. */
  display: 'block',
  color: theme.palette.text.secondary,
  fontSize: '2.5rem',

  [`&.${stepIconClasses.completed}`]: {
    color: 'inherit',

    [`& .${stepIconClasses.circle}`]: {
      backgroundColor: 'currentColor',
    },
  },

  [`&.${stepIconClasses.active}`]: {
    color: 'inherit',
  },
  [`&.${stepIconClasses.error}`]: {
    color: theme.palette.error.main,
  },

  [theme.breakpoints.up('md')]: {
    fontSize: '2rem',
  },

  /* Styles applied to the SVG text element. */
  [`& .${stepIconClasses.text}`]: {
    color: 'currentColor',
    fontSize: theme.typography.htmlFontSize,
    fontFamily: theme.typography.fontFamily,
    fontWeight: 600,
  },

  [`& .${stepIconClasses.check}`]: {
    color: '#fff',
  },

  /* Pseudo-class applied to the root element if `active={true}`. */
  [`& .${stepIconClasses.active}`]: {},

  /* Pseudo-class applied to the root element if `completed={true}`. */
  [`& .${stepIconClasses.completed}`]: {},

  /* Pseudo-class applied to the root element if `error={true}`. */
  [`& .${stepIconClasses.error}`]: {},

  // colorPrimary: {
  //   '&${stepIconClasses.completed}': {
  //     color: 'inherit',
  //   },
  //   '&${stepIconClasses.active}': {
  //     color: 'inherit',
  //   },
  // },
  // colorSecondary: {
  //   '&${stepIconClasses.completed}': {
  //     color: 'inherit',
  //   },
  //   '&${stepIconClasses.active}': {
  //     color: 'inherit',
  //   },
  // },
  [`& .${stepIconClasses.circle}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    borderRadius: '100rem',
    border: '3px solid currentColor',
  },
}));

// import { StepperContext } from '../Stepper/Stepper';

const StepIcon = React.forwardRef(function StepIcon(inProps, ref) {
  /**
   * MUI
   */
  const props = useThemeProps({ props: inProps, name: PREFIX });

  const { completed = false, icon, active = false, error = false } = props;

  const classes = useUtilityClasses({
    completed,
    icon,
    active,
    error,
    ...props,
  });

  // const { color = 'primary' } = useContext(StepperContext);

  if (typeof icon === 'number' || typeof icon === 'string') {
    const className = clsx(classes.root, {
      // [classes.colorPrimary]: color === 'primary',
      // [classes.colorSecondary]: color === 'secondary',
      [classes.active]: active,
      [classes.error]: error,
      [classes.completed]: completed,
    });

    if (error) {
      return <Warning className={className} ref={ref} />;
    }

    // if (active && isMdUp) {
    //   return <BlIcon icon="progress" className={className} ref={ref} />;
    // }

    return (
      <StyledIcon className={className} ref={ref}>
        <div className={classes.circle}>
          {completed && <BlIcon icon="check" className={classes.check} />}
          {!completed && <p className={classes.text}>{icon}</p>}
        </div>
      </StyledIcon>
    );
  }

  return icon;
});

StepIcon.propTypes = {
  /**
   * Whether this step is active.
   */
  active: PropTypes.bool,
  /**
   * Mark the step as completed. Is passed to child components.
   */
  completed: PropTypes.bool,
  /**
   * Mark the step as failed.
   */
  error: PropTypes.bool,
  /**
   * The label displayed in the step icon.
   */
  icon: PropTypes.node,
};

export default StepIcon;
